// Copyright (c) 2025 AssetCare.AI Inc. All rights reserved.

// JavaScript for handling application loading and responsiveness
const sidebar = document.getElementById('sidebar');
const mainContent = document.getElementById('mainContent');
const appIframe = document.getElementById('appIframe');
const mobileNavOverlay = document.getElementById('mobileNavOverlay');
const mobileNav = document.getElementById('mobileNav');
const sidebarToggle = document.getElementById('sidebarToggle');
const hamburgerMenu = document.getElementById('hamburgerMenu');
const closeMobileNav = document.getElementById('closeMobileNav');

// Variable to track if sidebar was manually collapsed by user
let isSidebarManuallyCollapsed = false;

// Kiosk mode globals
let kioskMode = false;
let kioskTimeout = null;
let currentKioskIndex = 0;

// Global function to render the user profile based on current state
window.renderUserProfile = function() {
    const isCollapsed = sidebar.classList.contains('collapsed-manual') ||
                        sidebar.classList.contains('collapsed-auto') ||
                        sidebar.classList.contains('collapsed-in-app');

    // Display for desktop/main sidebar
    const usernameMain = document.getElementById('usernameMain');
    const userProfilePictureMain = document.getElementById('userProfilePictureMain');
    const loginStatusMain = document.getElementById('loginStatusMain');
    const manageProfileButtonMain = document.getElementById('manageProfileButtonMain');
    const userProfileMain = document.querySelector('.sidebar .user-profile');

    // Display for mobile sidebar
    const usernameMobile = document.getElementById('usernameMobile');
    const userProfilePictureMobile = document.getElementById('userProfilePictureMobile');
    const loginStatusMobile = document.getElementById('loginStatusMobile');
    const manageProfileButtonMobile = document.getElementById('manageProfileButtonMobile');

    if (window.appState && window.appState.isAuthenticated) {
        const user = window.appState.user;
        const initials = user.given_name ? user.given_name.charAt(0) + (user.family_name ? user.family_name.charAt(0) : '') : user.preferred_username.substring(0, 2);
        
        // Main Sidebar
        usernameMain.textContent = isCollapsed ? initials.toUpperCase() : user.name;
        userProfilePictureMain.src = user.picture || 'images/user.png';
        loginStatusMain.innerHTML = '<a href="#" id="logoutButtonMain">Logout</a>';
        manageProfileButtonMain.style.display = isCollapsed ? 'none' : 'block';
        
        // Set tooltip for collapsed state
        userProfileMain.setAttribute('data-tooltip', isCollapsed ? `${user.name} (Logout)` : '');

        // Add click event to profile picture in collapsed state
        userProfilePictureMain.addEventListener('click', () => {
            if (isCollapsed) {
                window.appState.keycloak.logout();
            }
        });
        
        // Attach the event listener to the logout button
        const logoutButtonMain = document.getElementById('logoutButtonMain');
        if (logoutButtonMain) {
            logoutButtonMain.addEventListener('click', () => {
                window.appState.keycloak.logout();
            });
        }
        
        // Mobile Sidebar
        usernameMobile.textContent = user.name;
        userProfilePictureMobile.src = user.picture || 'images/user.png';
        loginStatusMobile.innerHTML = '<a href="#" id="logoutButtonMobile">Logout</a>';
        manageProfileButtonMobile.style.display = 'block';

        const logoutButtonMobile = document.getElementById('logoutButtonMobile');
        if (logoutButtonMobile) {
            logoutButtonMobile.addEventListener('click', () => {
                window.appState.keycloak.logout();
            });
        }
    } else {
        // Not logged in state
        const loginText = 'Log in';
        const notLoggedInText = 'Not logged in';

        // Main Sidebar
        usernameMain.textContent = notLoggedInText;
        userProfilePictureMain.src = 'images/user.png';
        loginStatusMain.innerHTML = `<a href="#" id="loginButtonMain">${loginText}</a>`;
        manageProfileButtonMain.style.display = 'none';
        
        // Set tooltip for collapsed state
        userProfileMain.setAttribute('data-tooltip', isCollapsed ? 'Log in' : '');

        // Add click event to profile picture in collapsed state
        userProfilePictureMain.addEventListener('click', () => {
            if (isCollapsed) {
                window.appState.keycloak.login();
            }
        });

        const loginButtonMain = document.getElementById('loginButtonMain');
        if (loginButtonMain) {
            loginButtonMain.addEventListener('click', () => {
                window.appState.keycloak.login();
            });
        }
        
        // Mobile Sidebar
        usernameMobile.textContent = notLoggedInText;
        userProfilePictureMobile.src = 'images/user.png';
        loginStatusMobile.innerHTML = `<a href="#" id="loginButtonMobile">${loginText}</a>`;
        manageProfileButtonMobile.style.display = 'none';

        const loginButtonMobile = document.getElementById('loginButtonMobile');
        if (loginButtonMobile) {
            loginButtonMobile.addEventListener('click', () => {
                window.appState.keycloak.login();
            });
        }
    }
}

// Helper function to retrieve the config entry for a given URL
// Supports both applications[] and kiosk.views[]
function getAppConfigByUrl(targetUrl) {
    if (!window.config) {
        return null;
    }

    // Check main applications
    let appConfig = window.config.applications?.find(app => app.url === targetUrl);
    if (appConfig) {
        return appConfig;
    }

    // Check kiosk views
    if (window.config.kiosk && window.config.kiosk.views) {
        appConfig = window.config.kiosk.views.find(view => view.url === targetUrl);
        if (appConfig) {
            return appConfig;
        }
    }

    return null;
}

// Function to load an application into the iframe
function loadApplication(url) {
    let finalUrl = url;
    
    // Get the idToken and the per-app config entry (this is the new token-passing logic)
    const idToken = window.appState && window.appState.idToken;
    const appConfig = getAppConfigByUrl(url);

    if (idToken && appConfig && appConfig.add_token) {
        const paramName = appConfig.add_token;
        
        // Prevent double-adding the token (same safety check the old Looker code had)
        if (!url.includes(paramName + '=')) {
            let params = `${paramName}=${encodeURIComponent(idToken)}`;

            // Only Looker-style embeds get the extra parameters.
            // This is now driven purely by the config value, not by URL string matching.
            if (paramName === 'id_token_hint') {
                const domain = window.location.hostname;
                params = `embed_domain=https://${encodeURIComponent(domain)}&allow_login_screen=true&prompt=none&${params}`;
            }

            const separator = url.includes('?') ? '&' : '?';
            finalUrl = url + separator + params;

            console.log(`Loaded URL with token param '${paramName}':`, finalUrl);
        } else {
            console.log(`Token param '${paramName}' already present in URL (pre-added by child app).`);
            finalUrl = url;
        }
    }

    appIframe.src = finalUrl;
    appIframe.classList.add('active'); // Show iframe
    mainContent.classList.add('in-app'); // Adjust main content layout
    sidebar.classList.add('collapsed-in-app'); // Collapse sidebar for app view

    // Only set active states if not in kiosk mode
    if (!kioskMode) {
        // Remove active state from all nav items
        document.querySelectorAll('.nav-item, .sub-nav-item').forEach(item => item.classList.remove('active'));
        // Set 'Applications' menu as active
        document.querySelectorAll('.applications-menu').forEach(item => item.classList.add('active'));
    }

    // The toggle button is now visible, allowing users to expand the menu
    window.renderUserProfile();

    if (window.innerWidth <= 768) { // If on mobile
        window.toggleMobileNav(); // Close mobile nav if open
    }
}

// Function to show the Home dashboard content
function showHomeContent() {
    if (!appIframe || !mainContent || !sidebar) {
        console.error('Required DOM elements not found');
        return;
    }
    appIframe.src = 'data:text/html,<html></html>'; // Clear iframe content gracefully
    appIframe.classList.remove('active'); // Hide iframe
    mainContent.classList.remove('in-app'); // Reset main content layout

    // Exit kiosk mode if active
    if (kioskMode) {
        kioskMode = false;
        if (kioskTimeout) {
            clearTimeout(kioskTimeout);
            kioskTimeout = null;
        }
        // Reset active state to none (will be set to home below)
        document.querySelectorAll('.nav-item, .sub-nav-item').forEach(item => item.classList.remove('active'));
    }

    // Revert sidebar to previous state or auto-collapse based on window width
    sidebar.classList.remove('collapsed-in-app'); // Remove in-app collapse
    
    if (isSidebarManuallyCollapsed) {
        sidebar.classList.add('collapsed-manual'); // Apply manual collapse if it was set
    } else {
        sidebar.classList.remove('collapsed-manual'); // Ensure manual collapse is off
    }

    // Always check auto-collapse for medium screens
    if (window.innerWidth <= 1024 && window.innerWidth > 768) {
        sidebar.classList.add('collapsed-auto');
    } else {
        sidebar.classList.remove('collapsed-auto');
    }
    
    // Set Home nav item as active
    document.querySelectorAll('.nav-item, .sub-nav-item').forEach(item => item.classList.remove('active'));
    document.querySelectorAll('.home-nav-item').forEach(item => item.classList.add('active'));
    
    window.renderUserProfile();
}

// Function to show the Applications section (currently same as Home)
window.showApplications = function() {
    window.showHomeContent(); // For now, clicking Applications just goes back to home
}

// Toggle mobile navigation
function toggleMobileNav() {
    mobileNavOverlay.classList.toggle('open'); // Control overlay visibility
    mobileNav.classList.toggle('open');        // Control menu slide-in animation
}

// Manual sidebar toggle (desktop/tablet only)
function toggleSidebarManual() {
    // Only allow manual toggle if not currently in an app (where collapse is forced)
    if (!mainContent.classList.contains('in-app')) {
        isSidebarManuallyCollapsed = !isSidebarManuallyCollapsed; // Toggle state variable
        sidebar.classList.toggle('collapsed-manual'); // Toggle class

        // Ensure auto-collapse class is removed if manually expanding
        if (!isSidebarManuallyCollapsed) {
            sidebar.classList.remove('collapsed-auto');
        } else {
            // If manually collapsing, also apply auto-collapse if conditions meet
            if (window.innerWidth <= 1024 && window.innerWidth > 768) {
                sidebar.classList.add('collapsed-auto');
            }
            // Collapse any expanded menus when collapsing sidebar
            document.querySelectorAll('.has-children.expanded').forEach(el => el.classList.remove('expanded'));
        }
        window.renderUserProfile();
    }
}

// Handle window resize for responsive sidebar collapsing and mobile nav
window.addEventListener('resize', () => {
    // Manage auto-collapse for medium screens
    if (window.innerWidth <= 1024 && window.innerWidth > 768) {
        if (!mainContent.classList.contains('in-app')) {
            sidebar.classList.add('collapsed-auto');
        }
        // Ensure manual collapse is still respected if set
        if (isSidebarManuallyCollapsed) {
            sidebar.classList.add('collapsed-manual');
        }
        // Collapse expanded menus if collapsing
        if (sidebar.classList.contains('collapsed-auto') || sidebar.classList.contains('collapsed-manual')) {
            document.querySelectorAll('.has-children.expanded').forEach(el => el.classList.remove('expanded'));
        }
    } else if (window.innerWidth > 1024) {
        // Expand fully on large screens unless manually collapsed or in-app
        sidebar.classList.remove('collapsed-auto');
        if (!mainContent.classList.contains('in-app') && !isSidebarManuallyCollapsed) {
            sidebar.classList.remove('collapsed-manual');
        }
    }

    // Handle mobile breakpoint
    if (window.innerWidth <= 768) { // If on mobile
        // Ensure mobile nav is hidden when resizing to mobile or staying on mobile
        mobileNavOverlay.classList.remove('open');
        mobileNav.classList.remove('open');
    }

    // If an app is currently active, ensure sidebar remains collapsed-in-app and toggle hidden
    if (mainContent.classList.contains('in-app')) {
        sidebar.classList.add('collapsed-in-app');
    }
    
    window.renderUserProfile();
});

// Function to check if a user has the required role
function hasRequiredRole(requiredRole) {
    // If no role is required, assume permission
    if (!requiredRole) {
        return true;
    }

    // Split the role string into resource and role name
    const [resource, role] = requiredRole.split(':');

    // Check if the user's resource_access object exists
    const resourceAccess = window.appState && window.appState.user && window.appState.user.resource_access;
    if (!resourceAccess) {
        //console.log("resource_access is not available for required role '" + requiredRole + "'");
        return false;
    }

    // Check if the resource and role exist in the user's permissions
    return resourceAccess[resource] && resourceAccess[resource].roles && resourceAccess[resource].roles.includes(role);
}

// Function to create an expandable menu item
function createExpandableMenuItem(text, iconClass, items, menuClass) {
    const menuItem = document.createElement('div');
    menuItem.className = 'nav-item has-children';
    if (menuClass) menuItem.classList.add(menuClass);
    menuItem.innerHTML = `<i class="fas ${iconClass}"></i><span>${text}</span><i class="fas fa-chevron-down chevron"></i>`;
    menuItem.setAttribute('data-tooltip', `View ${text.toLowerCase()} options`); // Add tooltip for parent menu

    // New code starts here to handle the sub-menu properly
    const subMenuWrapper = document.createElement('div');
    subMenuWrapper.className = 'sub-menu';

    items.forEach(item => {
        //console.log("Checking name '" + item.name + "' with required role '" + item.role + "'");
        // Only render the sub-item if the user has the required role
        if (hasRequiredRole(item.role)) {
            const subItem = document.createElement('a');
            subItem.className = 'sub-nav-item';
            subItem.textContent = item.name;
            subItem.setAttribute('data-url', item.url);
            subItem.setAttribute('data-open-in-new-tab', item.openInNewTab);
            subItem.setAttribute('data-tooltip', item.description); // Add tooltip with description
            subItem.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (item.openInNewTab) {
                    window.open(item.url, '_blank');
                } else {
                    loadApplication(item.url);
                    document.querySelectorAll('.nav-item, .sub-nav-item').forEach(el => el.classList.remove('active'));
                    subItem.classList.add('active');
                    menuItem.classList.add('active');
                }
            });
            subMenuWrapper.appendChild(subItem);
        }
    });

    // Only append the menu item if it contains sub-items the user has permission to see
    if (subMenuWrapper.children.length > 0) {
        menuItem.appendChild(subMenuWrapper);

        menuItem.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (sidebar.classList.contains('collapsed-in-app')) {
                return;
            }

            document.querySelectorAll('.has-children.expanded').forEach(el => {
                if (el !== menuItem) {
                    el.classList.remove('expanded');
                }
            });

            menuItem.classList.toggle('expanded');
        });
        return menuItem;
    }
    
    // Return null if no items are visible to the user
    return null;
}

// Function to start kiosk mode cycling
function startKiosk() {
    if (!window.config || !window.config.kiosk || !window.config.kiosk.views || window.config.kiosk.views.length === 0) {
        console.error('No kiosk configuration available.');
        return;
    }

    // Filter views by role permissions
    const kioskViews = window.config.kiosk.views.filter(view => !view.role || hasRequiredRole(view.role));
    if (kioskViews.length === 0) {
        console.error('No accessible kiosk views available.');
        return;
    }

    kioskMode = true;
    currentKioskIndex = 0;
    const duration = (window.config.kiosk.duration || 30) * 1000;

    // Clear any existing timeout
    if (kioskTimeout) {
        clearTimeout(kioskTimeout);
    }

    // Set Kiosk nav item as active
    document.querySelectorAll('.nav-item, .sub-nav-item').forEach(item => item.classList.remove('active'));
    document.querySelectorAll('.kiosk-nav-item').forEach(item => item.classList.add('active'));

    function cycle() {
        if (!kioskMode) return;

        const view = kioskViews[currentKioskIndex];
        loadApplication(view.url);

        currentKioskIndex = (currentKioskIndex + 1) % kioskViews.length;
        kioskTimeout = setTimeout(cycle, duration);
    }

    cycle();
    console.log(`Kiosk mode started: Cycling ${kioskViews.length} views every ${duration / 1000} seconds.`);
}

// Function to fetch config.json and render UI
window.loadConfigAndRenderUI = async function() {
    try {
        const response = await fetch('config.json');
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const config = await response.json();
        window.config = config; // Store globally for kiosk access

        // Render Welcome Section
        document.getElementById('welcomeTitle').textContent = config.welcome.title;
        document.getElementById('welcomeSubtitle').textContent = config.welcome.subtitle;

        // Render Applications tiles
        const appGrid = document.getElementById('appGrid');
        appGrid.innerHTML = ''; // Clear existing placeholders

        config.applications.forEach(app => {
            // Only render the app tile if the user has the required role
            if (hasRequiredRole(app.role)) {
                const appCard = document.createElement('a');
                appCard.href = "#"; // Default href
                appCard.className = "app-card";
                appCard.setAttribute('data-app-id', app.name.toLowerCase().replace(/\s/g, '-'));

                // Determine click behavior: open in new tab or load in iframe
                if (app.openInNewTab) {
                    appCard.onclick = () => {
                        window.open(app.url, '_blank');
                        return false; // Prevent default link behavior
                    };
                } else {
                    appCard.onclick = () => {
                        loadApplication(app.url);
                        // Set corresponding sidebar sub-item as active
                        document.querySelectorAll('.sub-nav-item').forEach(item => {
                            if (item.getAttribute('data-url') === app.url) {
                                document.querySelectorAll('.nav-item, .sub-nav-item').forEach(el => el.classList.remove('active'));
                                item.classList.add('active');
                                item.closest('.has-children').classList.add('active');
                            }
                        });
                        return false; // Prevent default link behavior
                    };
                }

                // Create icon/image element based on iconType
                let iconElement;
                if (app.iconType === 'image') {
                    iconElement = document.createElement('img');
                    iconElement.src = `images/${app.icon}`; // Path to local image
                    iconElement.alt = `${app.name} icon`; // Alt text for accessibility
                    iconElement.className = 'app-icon-image'; // Class for image styling
                } else { // Default to font-awesome
                    iconElement = document.createElement('i');
                    iconElement.className = app.icon; // Font Awesome class
                }
                appCard.appendChild(iconElement); // Append the created icon/image element

                const h3 = document.createElement('h3');
                h3.textContent = app.name;
                appCard.appendChild(h3);

                const p = document.createElement('p');
                p.textContent = app.description;
                appCard.appendChild(p);

                appGrid.appendChild(appCard);
            }
        });

        // Render dynamic sidebar menus
        const sidebarNavLinks = document.querySelector('.sidebar .nav-links');
        const mobileNavLinks = document.querySelector('.mobile-nav .nav-links');

        // Clear existing dynamic menus before re-rendering to prevent duplicates on reload.
        sidebarNavLinks.querySelectorAll('.applications-menu, .help-menu').forEach(el => el.remove());
        mobileNavLinks.querySelectorAll('.applications-menu, .help-menu').forEach(el => el.remove());

        // Applications menu
        const appMenu = createExpandableMenuItem('Applications', 'fa-th', config.applications, 'applications-menu');
        if (appMenu) sidebarNavLinks.appendChild(appMenu);

        const appMenuMobile = createExpandableMenuItem('Applications', 'fa-th', config.applications, 'applications-menu');
        if (appMenuMobile) mobileNavLinks.appendChild(appMenuMobile);
        // Help menu
        const helpMenu = createExpandableMenuItem('Help', 'fa-question-circle', config.help || [], 'help-menu');
        if (helpMenu) sidebarNavLinks.appendChild(helpMenu);
        
        const helpMenuMobile = createExpandableMenuItem('Help', 'fa-question-circle', config.help || [], 'help-menu');
        if (helpMenuMobile) mobileNavLinks.appendChild(helpMenuMobile);

        // Kiosk menu (simple nav item, only if configured)
        if (config.kiosk && config.kiosk.views && config.kiosk.views.length > 0) {
            const kioskItem = document.createElement('a');
            kioskItem.href = '#';
            kioskItem.className = 'nav-item kiosk-nav-item';
            kioskItem.innerHTML = '<i class="fas fa-tv"></i><span>Kiosk</span>';
            kioskItem.setAttribute('data-tooltip', 'Cycle through kiosk views');
            kioskItem.addEventListener('click', (e) => {
                e.preventDefault();
                startKiosk();
            });
            sidebarNavLinks.appendChild(kioskItem);

            // Mobile version
            const kioskItemMobile = kioskItem.cloneNode(true);
            kioskItemMobile.addEventListener('click', (e) => {
                e.preventDefault();
                startKiosk();
                toggleMobileNav(); // Close mobile nav after starting
            });
            mobileNavLinks.appendChild(kioskItemMobile);
        }

    } catch (error) {
        console.error('Error loading or parsing config.json:', error);
        // Display an error message to the user if config fails to load
        document.getElementById('welcomeTitle').textContent = 'Error loading applications.';
        document.getElementById('welcomeSubtitle').textContent = 'Please try again later or contact support.';
    }
}

// Initial state on page load and load config
window.addEventListener('load', () => {
    if (window.innerWidth <= 1024 && window.innerWidth > 768) {
        sidebar.classList.add('collapsed-auto');
    }
    // If initially on mobile, hide the desktop toggle
    if (window.innerWidth <= 768 && sidebarToggle) {
        sidebarToggle.style.display = 'none';
    }

    // Attach event listeners to the sidebar toggle button
    if (sidebarToggle) {
        sidebarToggle.addEventListener('click', toggleSidebarManual);
    }

    // Attach listeners to static home buttons. This makes the hardcoded "Home" button functional
    // and avoids creating a duplicate menu item.
    document.querySelectorAll('.home-nav-item').forEach(item => {
        item.addEventListener('click', (e) => {
            e.preventDefault();
            window.showHomeContent();
            // If the clicked item is inside the mobile nav, toggle it closed.
            if (item.closest('.mobile-nav')) {
                window.toggleMobileNav();
            }
        });
    });

    if (hamburgerMenu) {
        hamburgerMenu.addEventListener('click', window.toggleMobileNav);
    }

    if (closeMobileNav) {
        closeMobileNav.addEventListener('click', window.toggleMobileNav);
    }

    // The call to loadConfigAndRenderUI() has been removed from here.
    // It will now be called by authentication.js after login.
    
    // Call renderUserProfile after everything else is ready
    window.renderUserProfile();
});

// === EXPOSE FUNCTIONS TO CHILD IFRAMES (CRITICAL FOR IMAGE ANALYSIS APP) ===
if (typeof window !== 'undefined') {
    window.loadApplication = loadApplication;
    window.showHomeContent = showHomeContent;
    window.toggleMobileNav = toggleMobileNav;
    window.startKiosk = startKiosk; // Expose for potential future use
}